import { Button, VStack, Box, Text, Dialog, Portal, CloseButton } from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi'
import { useState, useEffect } from 'react'
import { FilterGroupCard } from '../listBuilder/FilterGroupCard'
import type { FilterGroup, FilterFieldMetadata, TagOption, FilterOperator } from '../../types/contactList'
import { uuid } from '../../lib/uuid'

interface FilterEditModalProps {
  isOpen: boolean
  onClose: () => void
  filterGroups: FilterGroup[]
  onApply: (groups: FilterGroup[]) => void
  fieldMetadata: FilterFieldMetadata[]
  tags: TagOption[]
}

export function FilterEditModal({
  isOpen,
  onClose,
  filterGroups,
  onApply,
  fieldMetadata,
  tags,
}: FilterEditModalProps) {
  const [draft, setDraft] = useState<FilterGroup[]>(filterGroups)

  // Reset draft when modal opens
  useEffect(() => {
    if (isOpen) setDraft(filterGroups)
  }, [isOpen, filterGroups])

  const handleGroupChange = (index: number, updated: FilterGroup) => {
    const newGroups = [...draft]
    newGroups[index] = updated
    setDraft(newGroups)
  }

  const handleRemoveGroup = (index: number) => {
    const newGroups = draft.filter((_, i) => i !== index)
    setDraft(newGroups.map((g, i) => ({ ...g, groupIndex: i })))
  }

  const handleAddGroup = () => {
    const newGroup: FilterGroup = {
      groupIndex: draft.length,
      filters: [
        {
          id: uuid(),
          fieldName: fieldMetadata[0]?.fieldName ?? '',
          operator: 'Equals' as FilterOperator,
          value: undefined,
          displayOrder: 0,
        },
      ],
    }
    setDraft([...draft, newGroup])
  }

  const handleApply = () => {
    onApply(draft)
    onClose()
  }

  return (
    <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="80vh">
            <Dialog.Header fontSize="md">Edit Filters</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={3} align="stretch">
                {draft.map((group, index) => (
                  <Box key={group.groupIndex}>
                    {index > 0 && (
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color="muted"
                        textTransform="uppercase"
                        textAlign="center"
                        my={1}
                      >
                        OR
                      </Text>
                    )}
                    <FilterGroupCard
                      group={group}
                      fields={fieldMetadata}
                      tags={tags}
                      onChange={(updated) => handleGroupChange(index, updated)}
                      onRemove={() => handleRemoveGroup(index)}
                      canRemove={draft.length > 1}
                    />
                  </Box>
                ))}

                <Button size="sm" variant="outline" colorPalette="brand" onClick={handleAddGroup}><FiPlus />Add Filter Group
                              </Button>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button size="sm" variant="ghost" mr={2} onClick={onClose}>
                Cancel
              </Button>
              <Button size="sm" colorPalette="brand" onClick={handleApply}>
                Apply Filters
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
